import React from 'react';
import { KaraokeOrderClientContext } from '../../context/KaraokeOrderClientContext';
import KaraokeOrderFormView1 from './KaraokeOrderFormView1';
import KaraokeOrderFormView3 from './KaraokeOrderFormView3';
import KaraokeOrderFormView2 from './KaraokeOrderFormView2';
import KaraokeOrderPriceInfo from './KaraokeOrderPriceInfo';
import useCustomForm, { validateField } from '../../../common/hooks/CustomForm';
import ValidationUtility from '../../../common/utility/ValidationUtility';
import { FieldValidationErrorsType, showErrorFlush } from '../../../common/form/FrekulForm';
import EmailUtility from '../../../common/utility/EmailUtility';

export default function KaraokeOrderForm({
  initialIsArtistLoggingIn,
  defaultCouponCode = '',
  balance = 0,
  completedCount,
  musicId = null,
  defaultTitle = null,
  defaultTitleKana = null,
  defaultArtistName = null,
  defaultArtistNameKana = null,
  defaultComposerName = null,
  defaultLyricWriterName = null,
  defaultApplicantName = null,
  basicAmount,
  royaltyOptionAmount,
  onvocalOptionAmount,
  duetOptionAmount,
  physicalOptionAmount,
  introClickOptionAmount,
} : {
  initialIsArtistLoggingIn: boolean,
  defaultCouponCode: string,
  balance: number,
  completedCount: number,
  musicId: number|null,
  defaultTitle: string|null,
  defaultTitleKana: string|null,
  defaultArtistName: string|null,
  defaultArtistNameKana: string|null,
  defaultComposerName: string|null,
  defaultLyricWriterName: string|null,
  defaultApplicantName: string|null,
  basicAmount: number,
  royaltyOptionAmount: number,
  onvocalOptionAmount: number,
  duetOptionAmount: number,
  physicalOptionAmount: number,
  introClickOptionAmount: number,
}) {
  const priceInfo = new KaraokeOrderPriceInfo(
    basicAmount,
    royaltyOptionAmount,
    onvocalOptionAmount,
    duetOptionAmount,
    physicalOptionAmount,
    introClickOptionAmount,
  );

  type ViewName = 'view1'|'view2'|'view3';
  type AjaxName = 'validate'|'validate2'|'loginOrRegister'|'musicInfo'|'submit';

  const [cardToken, setCardToken] = React.useState<string|null>(null);
  const [shouldAcquireCardToken, setShouldAcquireCardToken] = React.useState(false);
  const [view, setView] = React.useState<ViewName>('view1');
  const [isFirstView, setIsFirstView] = React.useState(true);
  const [ajax, setAjax] = React.useState<AjaxName|null>(null);
  const [isArtistLoggingIn, setIsArtistLoggingIn] = React.useState(initialIsArtistLoggingIn);
  const [paymentAmount, setPaymentAmount] = React.useState(priceInfo.basicAmount);
  const [billingAmount, setBillingAmount] = React.useState(priceInfo.basicAmount);
  const [discountAmount, setDiscountAmount] = React.useState(0);
  const [hasNonAppliedInitialOrderCoupon, setHasNonAppliedInitialOrderCoupon] = React.useState(false);

  const {
    validateFirstKaraokeOrder,
    validateKaraokeOrder,
    executeKaraokeOrder,
    loginOrRegister,
    getMusic,
  } = React.useContext(KaraokeOrderClientContext);

  type ArtistRegistrationFieldName =
    'artist_registration_type'|'artist_registration_mbmail'|
    'artist_registration_password'|'artist_registration_agreement';
  type ArtistRegistrationValuesType = { [name in ArtistRegistrationFieldName]: string };
  type ArtistRegistrationPartialErrorsType = { [name in ArtistRegistrationFieldName]?: FieldValidationErrorsType };
  type ArtistRegistrationErrorsType = { errors: ArtistRegistrationPartialErrorsType, warnings: ArtistRegistrationPartialErrorsType };
  const [
    artistRegistrationValues,
    artistRegistrationErrors,
    setArtistRegistrationValues,
    setArtistRegistrationErrors,
  ] = useCustomForm<
    ArtistRegistrationValuesType,
    ArtistRegistrationErrorsType,
    { name: ArtistRegistrationFieldName }
  >({
    defaultValue: {
      artist_registration_type: '',
      artist_registration_mbmail: '',
      artist_registration_password: '',
      artist_registration_agreement: '0',
    },
    validate(values, options, artistRegistrationErrors): ArtistRegistrationErrorsType {
      const name = options.name;
      const value = values[name];
      const error = validateField([
        ['artist_registration_type', 'notEmpty', 'Frekul登録について選択してください。', ValidationUtility.isNotEmptyText],
        ['artist_registration_mbmail', 'notEmpty', 'メールアドレスを入力してください。', (value) => values.artist_registration_type !== '' && ValidationUtility.isNotEmptyText(value)],
        ['artist_registration_mbmail', 'isValidEn', 'メールアドレスを半角英数字で入力してください。', (value) => values.artist_registration_type !== '' && ValidationUtility.isValidTextEn(value)],
        ['artist_registration_password', 'notEmpty', 'パスワードを入力してください。', (value) => values.artist_registration_type !== '' && ValidationUtility.isNotEmptyText(value)],
        ['artist_registration_password', 'isValidEn', 'パスワードを半角英数字で入力してください。', (value) => values.artist_registration_type !== '' && ValidationUtility.isValidTextEn(value)],
        ['artist_registration_agreement', 'notEmpty', 'Frekul利用規約に同意してください。', (value) => values.artist_registration_type === 'signup' && ValidationUtility.isNotEmptyText(value)],
      ], name, value);
      const warning = validateField([
        ['artist_registration_mbmail', 'notMobile', '携帯電話のメールアドレスではメールが受け取れない場合があります。メールの受信設定で、受信許可リストに「@frekul.com」を追加してからご利用ください。', (value) => values.artist_registration_type !== '' && !EmailUtility.isMobileEmail(value)],
      ], name, value);
      return {
        warnings: { ...artistRegistrationErrors?.warnings, [name]: warning },
        errors: { ...artistRegistrationErrors?.errors, [name]: error },
      };
    }
  });
  const isArtistRegistrationField = (name: string) => 
    name === 'artist_registration_type' ||
    name === 'artist_registration_mbmail' ||
    name === 'artist_registration_password' ||
    name === 'artist_registration_agreement';

  type FieldName = 
    'royalty_order'|'already_entrusted'|'onvocal'|'duet'|'intro_click'|'physical'|'coupon_code'|
    'title'|'title_kana'|'artist_name'|'artist_name_kana'|
    'lyric_writer_name'|'composer_name'|'applicant_name'|
    'use_vocal_synthesizer'|'vocal_synthesizer_name'|
    'payment_method'|'use_balance'|'agreement';
  type ValuesType = { [name in FieldName]: string };
  type ErrorsType = { [name in FieldName]?: FieldValidationErrorsType };
  const [values, errors, setValues, setErrors] = useCustomForm<ValuesType, ErrorsType, { name: FieldName }>({
    defaultValue: {
      royalty_order: '',
      already_entrusted: '0',
      physical: '',
      duet: '',
      onvocal: '',
      intro_click: '',
      coupon_code: defaultCouponCode || '',
      title: defaultTitle || '',
      title_kana: defaultTitleKana || '',
      artist_name: defaultArtistName || '',
      artist_name_kana: defaultArtistNameKana || '',
      lyric_writer_name: defaultLyricWriterName || '',
      composer_name: defaultComposerName || '',
      applicant_name: defaultApplicantName || '',
      use_vocal_synthesizer: '',
      vocal_synthesizer_name: '',
      payment_method: '',
      use_balance: '1',
      agreement: '0',
    },
    validate(values, options, errors): ErrorsType {
      const name = options.name;
      const value = values[name];
      const error = validateField([
        ['title', 'notEmpty', '曲名を入力してください。', ValidationUtility.isNotEmptyText],
        ['title_kana', 'notEmpty', '曲名読みを入力してください。', ValidationUtility.isNotEmptyText],
        ['title_kana', 'isValidKana', '曲名読みをひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['artist_name', 'notEmpty', 'アーティスト名を入力してください。', ValidationUtility.isNotEmptyText],
        ['artist_name_kana', 'notEmpty', 'アーティスト名読みを入力してください。', ValidationUtility.isNotEmptyText],
        ['artist_name_kana', 'isValidKana', 'アーティスト名読みをひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['lyric_writer_name', 'notEmpty', '作詞者名を入力してください。', ValidationUtility.isNotEmptyText],
        ['composer_name', 'notEmpty', '作曲者名を入力してください。', ValidationUtility.isNotEmptyText],
        ['applicant_name', 'notEmpty', '申込者名を入力してください。', ValidationUtility.isNotEmptyText],
        ['payment_method', 'notEmpty', 'お支払い方法を選択してください。', ValidationUtility.isNotEmptyText],
        ['coupon_code', 'isValidEn', 'クーポンコードは半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['agreement', 'notEmpty', '規約に同意してください。', ValidationUtility.isNotEmptyText],
      ], name, value);
      return { ...errors, [name]: error };
    }
  });

  React.useEffect(() => {
    if (ajax === 'validate') {
      validateFirstKaraokeOrder(values).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setDiscountAmount(responseData.discountAmount);
          setPaymentAmount(responseData.paymentAmount);
          setBillingAmount(responseData.billingAmount);
          setHasNonAppliedInitialOrderCoupon(responseData.hasNonAppliedInitialOrderCoupon);
          setView('view2');
          setIsFirstView(false);
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    } else if (ajax === 'validate2') {
      validateKaraokeOrder(values, cardToken).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setDiscountAmount(responseData.discountAmount);
          setPaymentAmount(responseData.paymentAmount);
          setBillingAmount(responseData.billingAmount);
          setHasNonAppliedInitialOrderCoupon(responseData.hasNonAppliedInitialOrderCoupon);
          setView('view3');
          setIsFirstView(false);
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    } else if (ajax === 'loginOrRegister') {
      loginOrRegister(artistRegistrationValues).then((responseData) => {
        setAjax(null);
        setArtistRegistrationErrors({
          warnings: {},
          errors: responseData.validationErrors || {}
        });
        if (responseData.result) {
          setIsArtistLoggingIn(true);
          setAjax('validate2');
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    } else if (ajax === 'musicInfo' && musicId !== null) {
      getMusic(musicId).then((responseData) => {
        if (responseData.result) {
          setValues({
            ...values,
            title: responseData.title,
            title_kana: responseData.title_kana,
            artist_name: responseData.artist_name,
            artist_name_kana: responseData.artist_name_kana,
            composer_name: responseData.composer_name,
            lyric_writer_name: responseData.lyric_writer_name,
          }, { name: 'title' });
        }
      });
    } else if (ajax === 'submit') {
      executeKaraokeOrder(values, cardToken).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          const { redirectUrl } = responseData;
          document.location.href = redirectUrl;
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    }
  }, [ajax]);

  return (
    <>
      {view === 'view1' && (
        <KaraokeOrderFormView1
          getValue={(name) => values[name]}
          setValue={(name, value) => {  
            setValues({
              ...values,
              // 連動して値を変更する
              already_entrusted: name === 'royalty_order' && values.already_entrusted === '1' ? '0' : values.already_entrusted,
              royalty_order: name === 'already_entrusted' && values.royalty_order === 'with_royalty' ? 'without_royalty' : values.royalty_order,
              [name]: value,
            }, { name });
          }}
          getErrors={(name) => errors?.[name] || {}}
          priceInfo={priceInfo}
          planAmount={priceInfo.getPlanAmount(values)}
          completedCount={completedCount}
          view={view}
          isFirstView={isFirstView}
          onNext={() => {
            setAjax('validate');
          }}
        />
      )}
      {view === 'view2' && (
        <KaraokeOrderFormView2
          getValue={(name) => {
            if (isArtistRegistrationField(name)) {
              return artistRegistrationValues[name];
            } else {
              return values[name];
            }
          }}
          setValue={(name, value) => {
            if (isArtistRegistrationField(name)) {
              setArtistRegistrationValues({ ...artistRegistrationValues, [name]: value }, { name });
            } else {
              setValues({ ...values, [name]: value }, { name });
            }
          }}
          getErrors={(name) => {
            if (isArtistRegistrationField(name)) {
              return artistRegistrationErrors?.errors[name] || {};
            } else {
              return errors?.[name] || {};
            }
          }}
          getWarnings={(name) => {
            if (isArtistRegistrationField(name)) {
              return artistRegistrationErrors?.warnings[name] || {};
            } else {
              return errors?.[name] || {};
            }
          }}
          priceInfo={priceInfo}
          planAmount={priceInfo.getPlanAmount(values)}
          shouldAcquireCardToken={shouldAcquireCardToken}
          balance={balance}
          discountAmount={discountAmount}
          isArtistLoggingIn={isArtistLoggingIn}
          hasNonAppliedInitialOrderCoupon={hasNonAppliedInitialOrderCoupon}
          view={view}
          onNext={() => {
            if (values.payment_method === 'cc_payment') {
              setCardToken(null);
              setShouldAcquireCardToken(true);
            } else if (isArtistLoggingIn) {
              setAjax('validate2');
            } else {
              setAjax('loginOrRegister');
            }
          }}
          onBack={() => {
            setView('view1');
            setIsFirstView(false);
          }}
          onCardTokenAcquired={(acquiredCardToken) => {
            setCardToken(acquiredCardToken);
            setShouldAcquireCardToken(false);
            if (isArtistLoggingIn) {
              setAjax('validate2');
            } else {
              setAjax('loginOrRegister');
            }
          }}
          onCardTokenError={(message) => {
            setCardToken(null);
            setShouldAcquireCardToken(false);
            showErrorFlush(message);
          }}
        />
      )}
      {view === 'view3' && (
        <KaraokeOrderFormView3
          getValue={(name) => values[name]}
          paymentAmount={paymentAmount}
          billingAmount={billingAmount}
          discountAmount={discountAmount}
          hasNonAppliedInitialOrderCoupon={hasNonAppliedInitialOrderCoupon}
          view={view}
          priceInfo={priceInfo}
          onBack={() => {
            setView('view2');
            setIsFirstView(false);
          }}
          onNext={() => setAjax('submit')}
        />
      )}
    </>
  );
}
